import { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icons';
import { formatStatus, statusStyle } from './statuses';
import { fieldOptionList } from './fieldOptions';
import { initialTransitionForm, saveStatusTransition } from './statusTransition';
import { plantDisplayName } from './nameUtils';

const DATE_LABEL = {
  sold: 'Sale Date',
  gifted: 'Gift Date',
  swapped: 'Swap Date',
  deceased: 'Date',
  loaned_in: 'Loan Date',
  loaned_out: 'Loan Date',
};

const PERSON_LABEL = {
  sold: 'Buyer',
  gifted: 'Recipient',
  swapped: 'Swapped With',
  loaned_in: 'Loaned From',
  loaned_out: 'Loaned To',
};

const SUPPORTED_STATUSES = new Set(Object.keys(DATE_LABEL));

function StatusTransitionSheet({ plant, targetStatus, statusByKey, fieldOptions, onSuccess, onCancel }) {
  const [form, setForm] = useState(initialTransitionForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const lossReasonOptions = fieldOptionList(fieldOptions, 'loss_reason');

  if (!SUPPORTED_STATUSES.has(targetStatus)) {
    console.error(`StatusTransitionSheet: unsupported targetStatus "${targetStatus}"`);
    return null;
  }
  const isLoan = targetStatus === 'loaned_in' || targetStatus === 'loaned_out';
  const isSwapped = targetStatus === 'swapped';
  const isDeceased = targetStatus === 'deceased';
  const isSold = targetStatus === 'sold';
  const showPerson = PERSON_LABEL[targetStatus] != null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveStatusTransition(plant.id, targetStatus, form);
      onSuccess();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return createPortal(
    <div className="round-sheet-backdrop" onClick={!saving ? onCancel : undefined}>
      <div className="round-sheet status-transition-sheet" onClick={e => e.stopPropagation()}>
        <div className="round-sheet-header">
          <div className="round-sheet-handle" />
          <button
            type="button"
            className="round-sheet-close"
            onClick={onCancel}
            aria-label="Close"
            title="Close"
            disabled={saving}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="status-transition-body">
          <p className="status-transition-title">
            Record as{' '}
            <span className="status-badge" style={statusStyle(targetStatus, statusByKey)}>
              {formatStatus(targetStatus, statusByKey)}
            </span>
          </p>
          <p className="status-transition-subtitle">
            {plantDisplayName(plant)} · {plant.specimen_id}
          </p>

          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">{DATE_LABEL[targetStatus] || 'Date'}</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="form-input"
                disabled={saving}
              />
            </div>

            {isSold && (
              <div className="form-field">
                <label className="form-label">Sale Price ($)</label>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  className="form-input"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  disabled={saving}
                />
              </div>
            )}

            {isDeceased && lossReasonOptions.length > 0 && (
              <div className="form-field">
                <label className="form-label">Loss Reason</label>
                <select
                  name="loss_reason"
                  value={form.loss_reason}
                  onChange={handleChange}
                  className="form-input"
                  disabled={saving}
                >
                  <option value="">— Unknown —</option>
                  {lossReasonOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}

            {showPerson && (
              <>
                <div className="form-field">
                  <label className="form-label">{PERSON_LABEL[targetStatus]}</label>
                  <input
                    name="person_name"
                    value={form.person_name}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional name"
                    disabled={saving}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Contact</label>
                  <input
                    name="person_contact"
                    value={form.person_contact}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional contact"
                    disabled={saving}
                  />
                </div>
              </>
            )}

            {isLoan && (
              <div className="form-field">
                <label className="form-label">Expected Return</label>
                <input
                  type="date"
                  name="expected_return_at"
                  value={form.expected_return_at}
                  onChange={handleChange}
                  className="form-input"
                  disabled={saving}
                />
              </div>
            )}

            {isSwapped && (
              <div className="form-field form-field-full">
                <label className="form-label">What did you receive?</label>
                <input
                  name="received_description"
                  value={form.received_description}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g. Philodendron gloriosum cutting"
                  disabled={saving}
                />
                <small className="field-hint">
                  After saving, use Status Details to add a new received plant or link an existing one.
                </small>
              </div>
            )}

            <div className="form-field form-field-full">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                className="form-input"
                rows="2"
                disabled={saving}
              />
            </div>
          </div>

          {error && <p className="error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : `Record as ${formatStatus(targetStatus, statusByKey)}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default StatusTransitionSheet;
